import { inject, Injectable } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { Pagination } from '@api/abstracts/models';
import { ListeCarteImageNomContext } from '@components/liste-carte-image-nom/models';
import { ListingCategoriesFacades } from '@components/listing-categories-facades/models';
import { ListingTutos } from '@components/listing-tutos/models';
import { Team } from '@components/slider-cartes-image-nom/models';
import { Time } from '@shared/constants';
import { Kitchen, LabelKey, Project, Store, StoreSchedule, StoreWithUuid } from '@shared/models';
import { DataService } from '@shared/services';
import { ProductTypeKey } from '@shared/services/elastic-search/mappings';
import {
  BlogArticle,
  BlogArticleDocument,
  BlogCategory,
  BlogCategoryDocument,
  CabinetBody,
  CabinetBodyDocument,
  Countertop,
  CountertopDocument,
  ElasticSearchResponse,
  Faq,
  FaqCategory,
  FaqCategoryDocument,
  FaqDocument,
  FilterPage,
  FilterPageDocument,
  Finition,
  FinitionDocument,
  Front,
  FrontDocument,
  FrontsCategoriesDocument,
  FurnitureImageName,
  FurnitureImageNameDescription,
  FurnitureImageNameDescriptionDocument,
  Handle,
  HandleDocument,
  KitchenDocument,
  LayoutDocument,
  NicheShelfDocument,
  Opportunity,
  OpportunityDocument,
  OtherFront,
  OtherFrontDocument,
  Pack,
  PackDocument,
  PayloadModels,
  Popin,
  PopinDocument,
  Promotion,
  PromotionDocument,
  ShowcaseDocument,
  StoreDocument,
  TeamDocument,
  Testimony,
  TestimonyDocument,
  TutoDocument,
} from '@shared/services/elastic-search/models';
import {
  GET_ACTIVE_POPINS_QUERY,
  GET_ALL_ACTIVE_STORES_QUERY,
  GET_ALL_STORE_OPPORTUNITIES_QUERY,
  GET_BLOG_ARTICLES_QUERY,
  GET_BLOG_CATEGORIES_QUERY,
  GET_CABINET_BODIES_QUERY,
  GET_COLORS_FILTER_QUERY,
  GET_COUNTERTOPS_FILTER_QUERY,
  GET_COUNTERTOPS_QUERY,
  GET_CUSTOM_KITCHENS_QUERY,
  GET_FAQ_CATEGORIES_QUERY,
  GET_FAQS_QUERY,
  GET_FEATURED_BLOG_ARTICLES_QUERY,
  GET_FILTER_PAGES_QUERY,
  GET_FINITIONS_FURNITURES_QUERY,
  GET_FINITIONS_QUERY,
  GET_FRONTS_CATEGORIES_QUERY,
  GET_FRONTS_FILTER_QUERY,
  GET_FRONTS_QUERY,
  GET_FURNITURES_IMAGE_NAME_DESCRIPTION_FILTER_QUERY,
  GET_FURNITURES_IMAGE_NAME_DESCRIPTION_QUERY,
  GET_FURNITURES_IMAGE_NAME_FILTER_QUERY,
  GET_FURNITURES_IMAGE_NAME_QUERY,
  GET_HANDLES_FILTER_QUERY,
  GET_HANDLES_QUERY,
  GET_KITCHENS_BY_MULTIPLE_FILTERS_QUERY,
  GET_KITCHENS_QUERY,
  GET_LATEST_TESTIMONIES_QUERY,
  GET_LAYOUTS_FILTER_QUERY,
  GET_OTHER_FRONTS_QUERY,
  GET_PACKS_QUERY,
  GET_PROMOTIONS_QUERY,
  GET_SHOWCASES_QUERY,
  GET_SINK_TAP_PACKS_FILTER_QUERY,
  GET_STORE_SCHEDULE_QUERY,
  GET_STORE_TEAMS_QUERY,
  GET_STORES_BY_AREA_QUERY,
  GET_STORES_NEAR_COORDINATES_QUERY,
  GET_TESTIMONIES_QUERY,
  GET_TUTOS_QUERY,
  ModelQueryOptions,
  ShowcaseQueryOptions,
} from '@shared/services/elastic-search/queries';
import {
  blogArticlesSerializer,
  blogCategoriesSerializer,
  cabinetBodiesSerializer,
  countertopsSerializer,
  faqCategoriesSerializer,
  faqsSerializer,
  filterColorsSerializer,
  filterGenericSerializer,
  filterHandlesSerializer,
  filterPagesSerializer,
  filterSinkTapPacksSerializer,
  finitionsSerializer,
  frontsCategoriesSerializer,
  frontsSerializer,
  furnituresImageNameDescriptionSerializer,
  furnituresImageNameSerializer,
  handlesSerializer,
  modelsSerializer,
  opportunitiesSerializer,
  otherFrontsSerializer,
  packsSerializer,
  popinsSerializer,
  promotionsSerializer,
  showcaseSerializer,
  storesScheduleSerializer,
  storesSerializer,
  teamsSerializer,
  testimoniesPaginatedSerializer,
  testimoniesSerializer,
  tutosSerializer,
} from '@shared/services/elastic-search/serializers';
import { GoogleService } from '@shared/services/google';
import { CurrentLanguageService, LanguageService } from '@shared/services/language';
import { LocalitySearch } from '@shared/services/locality-search/models';
import { envConfiguredNetworkId, objectToHashKey, secondsUntilMidnight, slugify } from '@shared/utils';
import { StoreLocatorStore } from '@stores';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ElasticSearchService extends ApiServiceBase<unknown> {
  private readonly mapFeaturesStore = inject(StoreLocatorStore);
  private readonly googleService = inject(GoogleService);
  private readonly dataService = inject(DataService);
  private readonly languageService = inject(LanguageService);

  constructor() {
    super(
      `elasticsearch?networkId=${envConfiguredNetworkId(inject(CurrentLanguageService).language.region)}`,
      'elasticsearch',
      'fournierWebservice'
    );
  }

  /**
   * Fetches the layout filter data from the ElasticSearch service.
   * The method performs a query using readElastic and processes the results through a specified serializer.
   */
  getLayoutFilter(): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<LayoutDocument>>(
      'LayoutFilter',
      GET_LAYOUTS_FILTER_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_layouts`,
      }
    ).pipe(map(filterGenericSerializer));
  }

  /**
   * Fetches a filtered list of colors based on the provided query and converts the response data.
   */
  getColorsFilter(): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'ColorsFilter',
      GET_COLORS_FILTER_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_colors`,
      }
    ).pipe(map(filterColorsSerializer));
  }

  /**
   * Retrieves a filtered list of sink tap packs and returns it as an observable of `LabelKey[]`.
   */
  getSinkTapPacksFilter(): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'SinkTapPacksFilter',
      GET_SINK_TAP_PACKS_FILTER_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_sink_tap_packs`,
      }
    ).pipe(map(filterSinkTapPacksSerializer));
  }

  /**
   * Retrieves the schedule of a specific store based on the provided store UUID.
   *
   * This method performs a read operation on the ElasticSearch database to fetch
   * the store schedule. The query result is then transformed using the `storesScheduleSerializer`
   * to return a well-structured schedule object.
   *
   * The data is cached for a predefined time period to enhance performance and reduce
   * redundant requests to the ElasticSearch database.
   *
   * @param storeUuid - The unique identifier of the store whose schedule is to be retrieved.
   *                    This value must be a valid string representing the store's UUID.
   * @returns An observable that emits the serialized store schedule as a `StoreSchedule` object.
   *          The observable completes after emitting the result or errors in case of issues during data retrieval.
   */
  getStoreSchedule(storeUuid: string): Observable<StoreSchedule> {
    return this.readElastic<ElasticSearchResponse<StoreDocument>>(
      'StoreSchedule',
      GET_STORE_SCHEDULE_QUERY(this.language.langcode, storeUuid),
      {
        cacheExpirationTime: Time.TEN_MINUTES,
        forcedKey: `es_${this.language.locale}_getStoreSchedule_${storeUuid}`,
      }
    ).pipe(map(storesScheduleSerializer));
  }

  /**
   * Retrieves the team members of a specific store, ordered by the weight defined in the middle-office.
   *
   * @param storeUuid - The unique identifier of the store whose team is to be retrieved.
   * @returns An observable that emits the serialized team members.
   */
  getStoreTeams(storeUuid: string): Observable<Team[]> {
    return this.readElastic<ElasticSearchResponse<TeamDocument>>(
      'StoreTeams',
      GET_STORE_TEAMS_QUERY(this.language.langcode, storeUuid),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getStoreTeams_${storeUuid}`,
      }
    ).pipe(map(teamsSerializer));
  }

  /**
   * Retrieves a list of kitchens based on the provided query.
   *
   * @param {PayloadModels} query - The query parameters used to filter and fetch the kitchens.
   */
  getKitchens(query: PayloadModels): Observable<Pagination<Kitchen>> {
    let isExtendedSearch = false;
    return objectToHashKey(query).pipe(
      switchMap((hashKey) => {
        return this.dataService.get$(`es_${this.language.locale}_getKitchens_${hashKey}`, 'CACHE').pipe(
          switchMap((cache) => {
            if (cache) {
              this.logs.debug('ElasticSearchService', '[CACHE] The kitchens were retrieved from the cache', {
                muted: true,
              });
              return of(cache as Pagination<Kitchen>);
            } else {
              const makeRequest = (q: PayloadModels) =>
                this.readElastic<ElasticSearchResponse<KitchenDocument>>(
                  'Kitchens',
                  GET_KITCHENS_QUERY(this.language.langcode, q, this.logs)
                );
              return makeRequest(query)
                .pipe(
                  switchMap((kitchens) => {
                    if (kitchens && kitchens.hits.total.value === 0) {
                      isExtendedSearch = true;
                      query.front = undefined;
                      return makeRequest(query).pipe(
                        switchMap((kitchens) => {
                          if (kitchens && kitchens.hits.total.value === 0) {
                            query.layout = undefined;
                            return makeRequest(query);
                          }
                          return of(kitchens);
                        })
                      );
                    }
                    return of(kitchens);
                  })
                )
                .pipe(
                  map((models) => {
                    const results = modelsSerializer(models, query, isExtendedSearch);
                    this.dataService
                      .set(`es_${this.language.locale}_getKitchens_${hashKey}`, results, Time.ONE_HOUR, 'CACHE')
                      .subscribe();
                    return results;
                  })
                );
            }
          })
        );
      })
    );
  }

  /**
   * Fetches and returns all active stores from the Elasticsearch database.
   */
  getAllStores(): Observable<Store[]> {
    const stores = this.mapFeaturesStore.select('stores')();
    if (stores && stores?.length > 0) {
      this.mapFeaturesStore.delete('listDisplayed');
      return of(stores);
    } else {
      return this.readElastic<ElasticSearchResponse<StoreDocument>>('AllStores', GET_ALL_ACTIVE_STORES_QUERY(), {
        cacheExpirationTime: Time.FOUR_HOURS,
        forcedKey: `es_${this.language.locale}_getAllStore`,
      }).pipe(
        map((response) => storesSerializer(response, this.language.langcode)),
        tap((stores) => {
          this.mapFeaturesStore.update('stores', stores);
          const storesForCurrentCountry = stores.filter((store) => store.langcode === this.language.langcode);
          if (storesForCurrentCountry.length === 1) {
            this.mapFeaturesStore.update('onlyStoreForCurrentCountry', storesForCurrentCountry[0]);
          }
        })
      );
    }
  }

  /**
   * Fetches a list of stores based on the provided location search criteria.
   *
   * @param {LocalitySearch} localitySearch - The location search criteria, which may include Google Place ID, coordinates, or other location properties.
   */
  getStoresByLocation(localitySearch: LocalitySearch): Observable<Store[]> {
    const getStoresNearCoordinatesQuery = (localitySearchWithCoordinates = localitySearch) =>
      objectToHashKey(localitySearch).pipe(
        switchMap((hashKey) => {
          const makeRequest = (searchRadiusKm = 50) =>
            this.readElastic<ElasticSearchResponse<StoreDocument>>(
              'StoresNearCoordinates',
              GET_STORES_NEAR_COORDINATES_QUERY(localitySearchWithCoordinates, {
                searchRadiusKm,
                sort: 'asc',
              }),
              {
                cacheExpirationTime: Time.FOUR_HOURS,
                forcedKey: `es_${this.language.locale}_getStores_${searchRadiusKm}_${hashKey}`,
              }
            );
          return makeRequest().pipe(
            switchMap((stores) => {
              if (stores && stores.hits.total.value === 0) {
                return makeRequest(1000);
              }
              return of(stores);
            }),
            map((response) => storesSerializer(response, this.language.langcode)),
            tap((stores) => {
              this.mapFeaturesStore.batchUpdate({
                update: { listDisplayed: stores },
                delete: ['mapStoreSelected'],
              });
            })
          );
        })
      );

    if (localitySearch.hasGooglePlaceId && !localitySearch.coordinates) {
      return this.googleService.fetchCoordinatesByGooglePlaceId(localitySearch.id).pipe(
        take(1),
        switchMap((coordinates) => {
          if (!coordinates) {
            return of([]);
          }
          const updatedLocalitySearch: LocalitySearch = {
            ...localitySearch,
            coordinates: {
              lat: coordinates.lat,
              lng: coordinates.lng,
            },
          };
          return getStoresNearCoordinatesQuery(updatedLocalitySearch);
        })
      );
    } else {
      return getStoresNearCoordinatesQuery();
    }
  }

  getStoreOpporunities(): Observable<Opportunity[]> {
    return this.readElastic<ElasticSearchResponse<OpportunityDocument>>(
      'StoreOpportunities',
      GET_ALL_STORE_OPPORTUNITIES_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.FOUR_HOURS,
        forcedKey: `es_${this.language.locale}_getStoreOpporunities`,
      }
    ).pipe(
      map(opportunitiesSerializer),
      tap((opportunities) => {
        this.mapFeaturesStore.update('opportunities', opportunities);
      })
    );
  }

  /**
   * Fetches a list of packs based on the specified pack type and optional filters.
   *
   * @param packType - The type of product pack to retrieve. This serves as a key to identify
   *                   the category of packs to fetch.
   * @param filters - An optional array of strings representing filters to refine the
   *                  query results. Default is an empty array if no filter is provided.
   *
   * @return An `Observable` that emits an array of `Pack` objects. The response is processed and
   *         serialized before being emitted.
   */
  getPacks(packType: ProductTypeKey, filters: string[] = []): Observable<Pack[]> {
    const keyParts = [`es_${this.language.locale}_getPacks`, packType];
    const filtersKey = slugify(filters.join('-'));
    if (filtersKey) keyParts.push(filtersKey);
    const cacheKey = keyParts.join('_');
    return this.readElastic<ElasticSearchResponse<PackDocument>>(
      'Packs',
      GET_PACKS_QUERY(this.language.langcode, packType, filters),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: cacheKey,
      }
    ).pipe(map(packsSerializer));
  }

  getFrontsCategories(): Observable<ListingCategoriesFacades> {
    return this.readElastic<ElasticSearchResponse<FrontsCategoriesDocument>>(
      'FrontsCategories',
      GET_FRONTS_CATEGORIES_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFrontsCategories`,
      }
    ).pipe(map(frontsCategoriesSerializer));
  }

  getFrontsFilter(): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'FrontsFilter',
      GET_FRONTS_FILTER_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_fronts`,
      }
    ).pipe(map(filterGenericSerializer));
  }

  getFronts(category: string, filters: string[] = []): Observable<Front[]> {
    const keyParts = [`es_${this.language.locale}_getFronts`, category];
    const filtersKey = slugify(filters.join('-'));
    if (filtersKey) keyParts.push(filtersKey);
    const cacheKey = keyParts.join('_');
    const productType = this.languageService.instant('ELASTIC_SEARCH.GET-FRONTS-QUERY.KITCHEN_FRONTS');

    return this.readElastic<ElasticSearchResponse<FrontDocument>>(
      'Fronts',
      GET_FRONTS_QUERY(this.language.langcode, productType, category, filters),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: cacheKey,
      }
    ).pipe(map(frontsSerializer));
  }

  getTutos(): Observable<ListingTutos> {
    return this.readElastic<ElasticSearchResponse<TutoDocument>>('Tutos', GET_TUTOS_QUERY(this.language.langcode), {
      cacheExpirationTime: Time.ONE_HOUR,
      forcedKey: `es_${this.language.locale}_getTutos`,
    }).pipe(map(tutosSerializer));
  }

  getFinitions(context: ListeCarteImageNomContext): Observable<Finition[]> {
    return this.readElastic<ElasticSearchResponse<FinitionDocument>>(
      'Finitions',
      context === 'meuble'
        ? GET_FINITIONS_FURNITURES_QUERY(this.language.langcode)
        : GET_FINITIONS_QUERY(this.language.langcode, context),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFinitions_${context}`,
      }
    ).pipe(map(finitionsSerializer));
  }

  getCountertopsFilter(): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'CountertopsFilter',
      GET_COUNTERTOPS_FILTER_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_countertops`,
      }
    ).pipe(map(filterGenericSerializer));
  }

  getCountertops(filters: string[] = []): Observable<Countertop[]> {
    const keyParts = [`es_${this.language.locale}_getCountertops`];
    const filtersKey = slugify(filters.join('-'));
    if (filtersKey) keyParts.push(filtersKey);
    const cacheKey = keyParts.join('_');
    const productType = this.languageService.instant('ELASTIC_SEARCH.GET-COUNTERTOPS-QUERY.WORKTOPS');

    return this.readElastic<ElasticSearchResponse<CountertopDocument>>(
      'Countertops',
      GET_COUNTERTOPS_QUERY(this.language.langcode, productType, filters),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: cacheKey,
      }
    ).pipe(map(countertopsSerializer));
  }

  getCabinetBodies(): Observable<CabinetBody[]> {
    const productType = this.languageService.instant('ELASTIC_SEARCH.GET-CABINET-BODIES-QUERY.CABINET_BODY');
    return this.readElastic<ElasticSearchResponse<CabinetBodyDocument>>(
      'CabinetBodies',
      GET_CABINET_BODIES_QUERY(this.language.langcode, productType),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getCabinetBodies`,
      }
    ).pipe(map(cabinetBodiesSerializer));
  }

  getHandlesFilter(): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'HandlesFilter',
      GET_HANDLES_FILTER_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_handles`,
      }
    ).pipe(map(filterHandlesSerializer));
  }

  getHandles(filters: string[] = []): Observable<Handle[]> {
    const keyParts = [`es_${this.language.locale}_getHandles`];
    const filterKey = slugify(filters.join('-'));
    if (filterKey) keyParts.push(filterKey);
    const cacheKey = keyParts.join('_');
    const productType = this.languageService.instant('ELASTIC_SEARCH.GET-HANDLES-QUERY.HANDLES');

    return this.readElastic<ElasticSearchResponse<HandleDocument>>(
      'Handles',
      GET_HANDLES_QUERY(this.language.langcode, productType, filters),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: cacheKey,
      }
    ).pipe(map(handlesSerializer));
  }

  getFurnituresImageNameFilter(productType: string): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'FurnituresImageNameFilter',
      GET_FURNITURES_IMAGE_NAME_FILTER_QUERY(this.language.langcode, productType),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_${slugify(productType)}`,
      }
    ).pipe(map(filterGenericSerializer));
  }

  getFurnituresImageName(productType: string, filters: string[] = []): Observable<FurnitureImageName[]> {
    const keyParts = [`es_${this.language.locale}_getFurnitures`, slugify(productType)];
    const filtersKey = slugify(filters.join('-'));
    if (filtersKey) keyParts.push(filtersKey);
    const cacheKey = keyParts.join('_');

    return this.readElastic<ElasticSearchResponse<NicheShelfDocument>>(
      'FurnituresImageName',
      GET_FURNITURES_IMAGE_NAME_QUERY(this.language.langcode, productType, filters),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: cacheKey,
      }
    ).pipe(map(furnituresImageNameSerializer));
  }

  getFurnituresImageNameDescriptionFilter(productType: string): Observable<LabelKey[]> {
    return this.readElastic<ElasticSearchResponse<unknown>>(
      'FurnituresImageNameDescriptionFilter',
      GET_FURNITURES_IMAGE_NAME_DESCRIPTION_FILTER_QUERY(this.language.langcode, productType),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilters_${slugify(productType)}`,
      }
    ).pipe(map(filterGenericSerializer));
  }

  getFurnituresImageNameDescription(
    productType: string,
    filters: string[] = []
  ): Observable<FurnitureImageNameDescription[]> {
    const keyParts = [`es_${this.language.locale}_getTopFurnitures`, slugify(productType)];
    const filtersKey = slugify(filters.join('-'));
    if (filtersKey) keyParts.push(filtersKey);
    const cacheKey = keyParts.join('_');

    return this.readElastic<ElasticSearchResponse<FurnitureImageNameDescriptionDocument>>(
      'FurnituresImageNameDescription',
      GET_FURNITURES_IMAGE_NAME_DESCRIPTION_QUERY(this.language.langcode, productType, filters),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: cacheKey,
      }
    ).pipe(map(furnituresImageNameDescriptionSerializer));
  }

  getPromotion(): Observable<Promotion> {
    return this.readElastic<ElasticSearchResponse<PromotionDocument>>(
      'Promotion',
      GET_PROMOTIONS_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getPromotion`,
      }
    ).pipe(map(promotionsSerializer));
  }

  getOtherFronts(notCategory: string): Observable<OtherFront[]> {
    return this.readElastic<ElasticSearchResponse<OtherFrontDocument>>(
      'OtherFronts',
      GET_OTHER_FRONTS_QUERY(this.language.langcode, notCategory)
    ).pipe(map(otherFrontsSerializer));
  }

  getBlogArticles(
    blogType: 'b2b' | 'b2c',
    from: number,
    size: number,
    level2Category?: string
  ): Observable<Pagination<BlogArticle>> {
    return this.readElastic<ElasticSearchResponse<BlogArticleDocument>>(
      'BlogArticles',
      GET_BLOG_ARTICLES_QUERY(this.language.langcode, blogType, from, size, level2Category),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getBlogArticles_${blogType}${level2Category ? `_${level2Category}` : ''}_from_${from}_size_${size}`,
      }
    ).pipe(map((articles) => blogArticlesSerializer(articles, size)));
  }

  getFeaturedBlogArticles(blogType: 'b2b' | 'b2c'): Observable<Pagination<BlogArticle>> {
    return this.readElastic<ElasticSearchResponse<BlogArticleDocument>>(
      'FeaturedBlogArticles',
      GET_FEATURED_BLOG_ARTICLES_QUERY(this.language.langcode, blogType),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFeaturedBlogArticles_${blogType}`,
      }
    ).pipe(map((articles) => blogArticlesSerializer(articles, 3)));
  }

  getFaqCategories(): Observable<FaqCategory[]> {
    return this.readElastic<ElasticSearchResponse<FaqCategoryDocument>>(
      'FaqCategories',
      GET_FAQ_CATEGORIES_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFaqCategories`,
      }
    ).pipe(map(faqCategoriesSerializer));
  }

  getFaqs(opts: {
    excludeFaqWithQuestion?: string;
    includeOnlyFaqWithIds?: string[];
    category?: string;
  }): Observable<Faq[]> {
    const keyParts = [`es_${this.language.locale}_getFaqs_`, slugify(opts.category ?? 'undefined')];
    const array = [
      ...['exclude'],
      ...(opts.excludeFaqWithQuestion ?? []),
      ...['include'],
      ...(opts.includeOnlyFaqWithIds ?? []),
    ];
    const filtersKey = slugify(array.join('-'));
    if (filtersKey) keyParts.push(filtersKey);
    const cacheKey = keyParts.join('_');
    return this.readElastic<ElasticSearchResponse<FaqDocument>>('Faqs', GET_FAQS_QUERY(this.language.langcode, opts), {
      cacheExpirationTime: Time.ONE_HOUR,
      forcedKey: cacheKey,
    }).pipe(map(faqsSerializer));
  }

  getBlogCategories(): Observable<BlogCategory[]> {
    return this.readElastic<ElasticSearchResponse<BlogCategoryDocument>>(
      'BlogCategories',
      GET_BLOG_CATEGORIES_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getBlogCategories`,
      }
    ).pipe(map(blogCategoriesSerializer));
  }

  getLatestTestimonies(nTestimonies: number, excludeUrl: string): Observable<Testimony[]> {
    return this.readElastic<ElasticSearchResponse<TestimonyDocument>>(
      'LatestTestimonies',
      GET_LATEST_TESTIMONIES_QUERY(this.language.langcode, nTestimonies, excludeUrl),
      {
        cacheExpirationTime: Time.FOUR_HOURS,
        forcedKey: `es_${this.language.locale}_getLatestTestimonies_x${nTestimonies}_exclude-${excludeUrl}`,
      }
    ).pipe(map(testimoniesSerializer));
  }

  getTestimonies(from: number, size: number): Observable<Pagination<Testimony>> {
    return this.readElastic<ElasticSearchResponse<TestimonyDocument>>(
      'Testimonies',
      GET_TESTIMONIES_QUERY(this.language.langcode, from, size),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getTestimonies_from_${from}_size_${size}`,
      }
    ).pipe(map((testimonies) => testimoniesPaginatedSerializer(testimonies, size)));
  }

  getFilterPages(): Observable<FilterPage[]> {
    return this.readElastic<ElasticSearchResponse<FilterPageDocument>>(
      'FilterPages',
      GET_FILTER_PAGES_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getFilterPages`,
      }
    ).pipe(map(filterPagesSerializer));
  }

  getStoresByArea(type: 'region' | 'dept' | 'all', area: string): Observable<StoreWithUuid[]> {
    return this.readElastic<ElasticSearchResponse<StoreDocument>>(
      'StoresByArea',
      GET_STORES_BY_AREA_QUERY(this.language.langcode, type, area),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getStoresByArea_${type}_${slugify(area)}`,
      }
    ).pipe(map((response) => storesSerializer(response, this.language.langcode) as StoreWithUuid[]));
  }

  getShowcases(options: ShowcaseQueryOptions): Observable<{ showcases: Project[]; total: number }> {
    const response = this.readElastic<ElasticSearchResponse<ShowcaseDocument>>(
      'Showcases',
      GET_SHOWCASES_QUERY(this.language.langcode, options),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getShowcases_${JSON.stringify(options)}`,
      }
    );
    return response.pipe(
      map((res) => ({
        showcases: showcaseSerializer(res),
        total: res.hits.total.value,
      }))
    );
  }

  getKitchensByMultipleFilters(options: ModelQueryOptions): Observable<Pagination<Kitchen>> {
    return this.readElastic<ElasticSearchResponse<KitchenDocument>>(
      'KitchensByMultipleFilters',
      GET_KITCHENS_BY_MULTIPLE_FILTERS_QUERY(this.language.langcode, options),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getModels_${JSON.stringify(options)}`,
      }
    ).pipe(
      map((response) =>
        modelsSerializer(
          response,
          {
            itemFrom: options.from,
            itemsToFetch: options.size,
          },
          false
        )
      )
    );
  }

  getCustomKitchens(): Observable<Finition[]> {
    return this.readElastic<ElasticSearchResponse<FinitionDocument>>(
      'CustomKitchens',
      GET_CUSTOM_KITCHENS_QUERY(this.language.langcode),
      {
        cacheExpirationTime: Time.ONE_HOUR,
        forcedKey: `es_${this.language.locale}_getCustomKitchens`,
      }
    ).pipe(map(finitionsSerializer));
  }

  getActivePopins(): Observable<Popin[]> {
    return this.readElastic<ElasticSearchResponse<PopinDocument>>(
      'ActivePopins',
      GET_ACTIVE_POPINS_QUERY(this.language.langcode),
      {
        cacheExpirationTime: secondsUntilMidnight(),
        forcedKey: `es_${this.language.locale}_getActivePopins`,
      }
    ).pipe(map(popinsSerializer));
  }
}
